import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { usePageTitle } from '../../hooks/usePageTitle';
import { AttendanceStatus } from '../../types/event';
import type { FamilyEvent } from '../../types/family';
import { useFamilyAgenda } from '../../components/family/useFamilyAgenda';
import { Group, LoadingRows, PageTitle } from '../../components/family/ui';
import EventRow from '../../components/family/EventRow';
import { addDays, dayLabel, dayOffset, isoDate, shortNames } from '../../components/family/format';

type Period = 'upcoming' | 'past';

const UPCOMING_DAYS = 90;
const PAST_DAYS = 120;

/** Napok szerint csoportosítva, a nap fejléce a csoport felett. */
const groupByDay = (events: FamilyEvent[]) => {
  const groups: { key: number; label: string; events: FamilyEvent[] }[] = [];
  events.forEach((e) => {
    const key = dayOffset(e.startTime);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.events.push(e);
    else groups.push({ key, label: dayLabel(e.startTime), events: [e] });
  });
  return groups;
};

/**
 * A gyerek(ek) programja egy listában: edzések, meccsek, egyéb események.
 * Közelgő és korábbi nézet; több gyereknél szűrhető gyerekre. Egy sorra
 * koppintva nyílik az esemény (visszajelzés, utazás, felállás, eredmény).
 */
const FamilyProgram: React.FC = () => {
  usePageTitle('Program');
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<Period>('upcoming');
  const [childId, setChildId] = useState<number | null>(null);
  const onlyUnanswered = searchParams.get('filter') === 'unanswered';

  const range = useMemo(() => {
    const today = new Date();
    return period === 'upcoming'
      ? { from: isoDate(today), to: isoDate(addDays(today, UPCOMING_DAYS)) }
      : { from: isoDate(addDays(today, -PAST_DAYS)), to: isoDate(today) };
  }, [period]);
  const { agenda, error } = useFamilyAgenda(range.from, range.to);

  const players = agenda?.players ?? [];
  const multiple = players.length > 1;
  const names = shortNames(players);

  const events = useMemo(() => {
    const now = new Date();
    let list = (agenda?.events ?? []).filter((e) =>
      period === 'upcoming' ? new Date(e.endTime) > now : new Date(e.endTime) <= now
    );
    if (childId) list = list.filter((e) => e.myPlayers.some((p) => p.playerId === childId));
    if (onlyUnanswered && period === 'upcoming') {
      list = list.filter(
        (e) => !e.isCancelled && e.responsesEnabled && e.myPlayers.some((p) => p.inSquad && p.status === AttendanceStatus.NoResponse)
      );
    }
    return period === 'past' ? [...list].reverse() : list;
  }, [agenda, period, childId, onlyUnanswered]);

  const groups = groupByDay(events);

  const chip = (active: boolean) =>
    `shrink-0 rounded-full px-3.5 py-1.5 text-[15px] font-medium transition-colors ${
      active ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 ring-1 ring-black/5'
    }`;

  return (
    <div>
      <PageTitle title="Program" />

      <div className="mb-3 grid grid-cols-2 rounded-xl bg-gray-200/70 p-1" role="tablist">
        {([
          ['upcoming', 'Közelgő'],
          ['past', 'Korábbi'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={period === key}
            onClick={() => setPeriod(key)}
            className={`rounded-lg py-1.5 text-[15px] font-medium transition-colors ${
              period === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {(multiple || (onlyUnanswered && period === 'upcoming')) && (
        <div className="-mx-4 mb-3 flex gap-2 overflow-x-auto px-4 pb-1">
          {multiple && (
            <>
              <button type="button" onClick={() => setChildId(null)} className={chip(childId === null)}>
                Mindenki
              </button>
              {players.map((p) => (
                <button key={p.playerId} type="button" onClick={() => setChildId(p.playerId)} className={chip(childId === p.playerId)}>
                  {names[p.playerId]}
                </button>
              ))}
            </>
          )}
          {onlyUnanswered && period === 'upcoming' && (
            <button
              type="button"
              onClick={() => {
                searchParams.delete('filter');
                setSearchParams(searchParams, { replace: true });
              }}
              className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 py-1.5 pl-3.5 pr-2.5 text-[15px] font-medium text-amber-900"
            >
              Amire nem jeleztél
              <XMarkIcon className="h-4 w-4" aria-label="Szűrés törlése" />
            </button>
          )}
        </div>
      )}

      {error && !agenda && (
        <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
          Most nem sikerült betölteni a programot. Próbáld újra kicsit később.
        </p>
      )}

      {!agenda && !error && <LoadingRows rows={4} />}

      {agenda && groups.length === 0 && (
        <p className="rounded-2xl bg-white p-6 text-center text-[15px] text-gray-500 ring-1 ring-black/5">
          {onlyUnanswered && period === 'upcoming'
            ? 'Mindenre jeleztél.'
            : period === 'upcoming'
              ? 'Nincs közelgő program.'
              : 'Az elmúlt hónapokban nem volt program.'}
        </p>
      )}

      <div className="space-y-5">
        {groups.map((group) => (
          <section key={group.key}>
            <h2 className="mb-1.5 px-1 text-[13px] font-semibold uppercase tracking-wide text-gray-500">{group.label}</h2>
            <Group>
              {group.events.map((e) => (
                <EventRow key={e.id} event={e} showNames={multiple && !childId} />
              ))}
            </Group>
          </section>
        ))}
      </div>
    </div>
  );
};

export default FamilyProgram;
